import React, { useMemo } from 'react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { TrendingDown, Calendar } from 'lucide-react';

const AmortizationSchedule = ({ currentBalance, interestRate, yearsRemaining, paymentFrequency }) => {
  const [viewMode, setViewMode] = React.useState('chart'); // chart or table
  
  const scheduleData = useMemo(() => {
    if (!currentBalance || !interestRate || !yearsRemaining) return [];
    
    const periodsPerYear = paymentFrequency === 'monthly' ? 12 : paymentFrequency === 'biweekly' ? 26 : 52;
    const monthlyRate = interestRate / 100 / 12;
    const totalPeriods = yearsRemaining * periodsPerYear;
    
    // Calculate payment amount
    const payment = currentBalance * (monthlyRate * Math.pow(1 + monthlyRate, totalPeriods)) / 
                    (Math.pow(1 + monthlyRate, totalPeriods) - 1);
    
    let balance = currentBalance;
    const schedule = [];
    const currentYear = new Date().getFullYear();
    
    // Generate year-by-year summary
    for (let year = 0; year < yearsRemaining; year++) {
      let yearPrincipal = 0;
      let yearInterest = 0;
      const periodsThisYear = Math.min(periodsPerYear, totalPeriods - (year * periodsPerYear));
      
      for (let period = 0; period < periodsThisYear; period++) {
        const interestPayment = balance * monthlyRate;
        const principalPayment = payment - interestPayment;
        
        yearInterest += interestPayment;
        yearPrincipal += principalPayment;
        balance -= principalPayment;
      }
      
      schedule.push({
        year: currentYear + year,
        yearLabel: `Year ${year + 1}`,
        principal: yearPrincipal,
        interest: yearInterest,
        totalPayment: yearPrincipal + yearInterest,
        remainingBalance: Math.max(0, balance),
        cumulativePrincipal: currentBalance - balance
      });
      
      if (balance <= 0) break;
    }
    
    return schedule;
  }, [currentBalance, interestRate, yearsRemaining, paymentFrequency]);
  
  if (scheduleData.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        <p>Enter mortgage details to see amortization schedule</p>
      </div>
    );
  }
  
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h4 className="text-lg font-heading font-semibold flex items-center gap-2">
          <TrendingDown className="h-5 w-5 text-primary" />
          Amortization Schedule
        </h4>
        <div className="flex gap-2">
          <button
            onClick={() => setViewMode('chart')}
            className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
              viewMode === 'chart'
                ? 'bg-primary text-primary-foreground'
                : 'bg-background border border-border hover:bg-muted'
            }`}
          >
            Chart
          </button>
          <button
            onClick={() => setViewMode('table')}
            className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
              viewMode === 'table'
                ? 'bg-primary text-primary-foreground'
                : 'bg-background border border-border hover:bg-muted'
            }`}
          >
            Table
          </button>
        </div>
      </div>
      
      {viewMode === 'chart' && (
        <div className="space-y-6">
          {/* Principal vs Interest Chart */}
          <div className="bg-card border border-border rounded-xl p-4">
            <h5 className="text-sm font-semibold mb-4">Principal vs Interest Payments</h5>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={scheduleData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                <XAxis 
                  dataKey="yearLabel" 
                  stroke="#94A3B8"
                  style={{ fontSize: '12px' }}
                />
                <YAxis 
                  stroke="#94A3B8"
                  style={{ fontSize: '12px' }}
                  tickFormatter={(value) => `$${(value / 1000).toFixed(0)}K`}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#1E293B',
                    border: '1px solid #334155',
                    borderRadius: '8px',
                    color: '#F8FAFC'
                  }}
                  formatter={(value) => `$${value.toLocaleString('en-CA', {maximumFractionDigits: 0})}`}
                />
                <Legend />
                <Bar dataKey="principal" fill="#3DD68C" name="Principal" />
                <Bar dataKey="interest" fill="#EF4444" name="Interest" />
              </BarChart>
            </ResponsiveContainer>
          </div>
          
          {/* Balance Over Time */}
          <div className="bg-card border border-border rounded-xl p-4">
            <h5 className="text-sm font-semibold mb-4">Remaining Balance Over Time</h5>
            <ResponsiveContainer width="100%" height={250}>
              <LineChart data={scheduleData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                <XAxis 
                  dataKey="yearLabel" 
                  stroke="#94A3B8"
                  style={{ fontSize: '12px' }}
                />
                <YAxis 
                  stroke="#94A3B8"
                  style={{ fontSize: '12px' }}
                  tickFormatter={(value) => `$${(value / 1000).toFixed(0)}K`}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#1E293B',
                    border: '1px solid #334155',
                    borderRadius: '8px',
                    color: '#F8FAFC'
                  }}
                  formatter={(value) => `$${value.toLocaleString('en-CA', {maximumFractionDigits: 0})}`}
                />
                <Line 
                  type="monotone" 
                  dataKey="remainingBalance" 
                  stroke="#3DD68C" 
                  strokeWidth={3}
                  name="Balance"
                  dot={{ fill: '#3DD68C', r: 4 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
      
      {viewMode === 'table' && (
        <div className="bg-card border border-border rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-muted/50">
                <tr>
                  <th className="text-left px-4 py-3 text-sm font-semibold">Year</th>
                  <th className="text-right px-4 py-3 text-sm font-semibold">Principal</th>
                  <th className="text-right px-4 py-3 text-sm font-semibold">Interest</th>
                  <th className="text-right px-4 py-3 text-sm font-semibold">Total Payment</th>
                  <th className="text-right px-4 py-3 text-sm font-semibold">Remaining Balance</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {scheduleData.map((row) => (
                  <tr key={row.year} className="hover:bg-muted/30 transition-colors">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <Calendar className="h-4 w-4 text-muted-foreground" />
                        <span className="font-medium">{row.year}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right font-mono text-sm">
                      ${row.principal.toLocaleString('en-CA', {maximumFractionDigits: 0})}
                    </td>
                    <td className="px-4 py-3 text-right font-mono text-sm text-red-400">
                      ${row.interest.toLocaleString('en-CA', {maximumFractionDigits: 0})}
                    </td>
                    <td className="px-4 py-3 text-right font-mono text-sm font-semibold">
                      ${row.totalPayment.toLocaleString('en-CA', {maximumFractionDigits: 0})}
                    </td>
                    <td className="px-4 py-3 text-right font-mono text-sm">
                      ${row.remainingBalance.toLocaleString('en-CA', {maximumFractionDigits: 0})}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default AmortizationSchedule;
